import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Hero } from '@/components/home/Hero';
import { FeaturedArticles } from '@/components/home/FeaturedArticles';
import { QuizPromoSection } from '@/components/home/QuizPromoSection';
import { TopProducts } from '@/components/home/TopProducts';
import { Categories } from '@/components/home/Categories';
import { Testimonials } from '@/components/home/Testimonials';
import { TrustSection } from '@/components/home/TrustSection';
import { Newsletter } from '@/components/home/Newsletter';

const Index = () => {
  // Fetch featured/published articles
  const { data: articlesData = [], isLoading: articlesLoading } = useQuery({
    queryKey: ['featured-articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          slug,
          excerpt,
          featured_image,
          published_at,
          updated_at,
          categories:category_id (
            name,
            slug
          )
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      return data || [];
    },
  });

  // Transform to expected format
  const articles = articlesData.map((article: any) => ({
    ...article,
    category: article.categories,
  }));

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch top products (best sellers or most recent)
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['top-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('is_best_seller', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch active testimonials
  const { data: testimonials = [], isLoading: testimonialsLoading } = useQuery({
    queryKey: ['testimonials'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Organization Schema for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Silla Ideal",
    "url": "https://silla-ideal.com",
    "logo": "https://silla-ideal.com/logo.svg",
    "description": "Guías independientes y comparativas de sillas de escritorio ergonómicas. Análisis objetivos para encontrar tu silla perfecta.",
    "sameAs": []
  };

  return (
    <Layout>
      <Helmet>
        <title>Silla Ideal - Guías de Sillas de Escritorio 2025</title>
        <meta 
          name="description" 
          content="Análisis independientes de sillas ergonómicas. Comparativas, rankings y el quiz para encontrar tu silla perfecta según tu altura, peso y necesidades." 
        />
        <link rel="canonical" href="https://silla-ideal.com/" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <Hero />
      <FeaturedArticles articles={articles} isLoading={articlesLoading} />
      <QuizPromoSection />
      <TopProducts products={products} isLoading={productsLoading} />
      <Categories categories={categories} isLoading={categoriesLoading} />
      <TrustSection />
      <Testimonials testimonials={testimonials} isLoading={testimonialsLoading} />
      <Newsletter />
    </Layout>
  );
};

export default Index;
